/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { JimboResponse, LoqateFullAddress } from '@models';
import { useCreateCustomerAddressMutation, useGetSuburbsListQuery } from '@services';
import { upsertSelectedCustomerAddress } from '@store/slices/customer';
import { addErrorToLog } from '@store/slices/error-log';
import { showToast } from '@store/slices/notification';
import { fillPostcode } from '@pages/Ava/helpers/postcodes';
import { AddNewAddressForm } from './AddNewAddressForm';
import { AddressFormData, AU_STATES, LoqateFullAddressInitialState } from '@constants';
import { AddNewAddressAutocomplete } from './AddNewAddressAutocomplete';
import { v4 as uuidv4 } from 'uuid';

const schema = yup.object({
  address1: yup.string().required('Address 1 is required.'),
  address2: yup.string(),
  address_type: yup.string().required('Address type is required.'),
  city: yup.string().required('City or suburb is required.'),
  company: yup.string(),
  postal_code: yup.string().required('Postal code is required.'),
  state_or_province: yup.string().required('State is required.'),
  longitude: yup.string().required('Longitude is required.'),
  latitude: yup.string().required('Latitude is required.'),
  gnafid: yup.string(),
});

const AddNewAddress: FC = () => {
  const [createCustomerAddress, { isLoading }] = useCreateCustomerAddressMutation();
  const [loqateFullAddress, setLoqateFullAddress] = useState<LoqateFullAddress>(LoqateFullAddressInitialState);
  const [resetAutoComplete, setResetComplete] = useState<boolean>(false);
  const [isFormVisible, setFormVisible] = useState<boolean>(false);

  const { data: suburbs } = useGetSuburbsListQuery();

  const availableSuburbs = suburbs && fillPostcode(suburbs);

  const dispatch = useAppDispatch();

  const customer = useAppSelector((state) => state.customer.value.selectedCustomer!);

  const {
    control,
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AddressFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      address_type: 'residential',
      state_or_province: AU_STATES[0].id,
    },
  });

  const onSubmit: SubmitHandler<AddressFormData> = (formData: AddressFormData) => {
    const getState = AU_STATES.find((item) => item.id === formData.state_or_province);

    const newAddress = {
      ...formData,
      country_code: 'AU',
      customer_id: customer.id,
      first_name: customer.first_name,
      last_name: customer.last_name,
      phone: customer.phone,
      state_or_province: getState?.value ?? AU_STATES[0].value,
      form_fields: [
        {
          name: 'Geo.Long',
          value: formData.longitude ?? '',
        },
        {
          name: 'Geo.Lat',
          value: formData.latitude ?? '',
        },
        {
          name: 'GNAFPID',
          value: formData.gnafid ?? `JIMBO|${uuidv4()}`, // add a random GNAFID if it's not provided for PWA
        },
        {
          name: 'State Abbreviation',
          value: getState?.id ?? '',
        },
      ],
    };

    createCustomerAddress(newAddress)
      .unwrap()
      .then((data) => {
        dispatch(
          showToast({
            message: `Successfully created new address details for ${customer.first_name}!`,
            type: 'success',
          }),
        );

        data && dispatch(upsertSelectedCustomerAddress(data));

        reset({
          address1: '',
          address2: '',
          city: '',
          state_or_province: '',
          postal_code: '',
          longitude: '',
          latitude: '',
          gnafid: '',
        });

        setLoqateFullAddress(LoqateFullAddressInitialState);

        setResetComplete((pvalue) => !pvalue);
      })
      .catch((error: { data: JimboResponse }) => {
        dispatch(addErrorToLog(error?.data));

        dispatch(
          showToast({
            message: `Failed to create new address details for ${customer.first_name}!`,
            type: 'error',
          }),
        );
      });
  };

  const toggleManualForm = () => {
    setFormVisible(!isFormVisible);
  };

  return (
    <>
      {!isFormVisible && (
        <AddNewAddressAutocomplete
          availableSuburbs={availableSuburbs ?? []}
          loqateFullAddress={loqateFullAddress}
          customer={customer}
          onSubmit={onSubmit}
          resetAutoComplete={resetAutoComplete}
          setLoqateFullAddress={setLoqateFullAddress}
        />
      )}
      {!isFormVisible && !resetAutoComplete && <Typography variant="h6"> - OR -</Typography>}
      <Box pt={isFormVisible ? 0 : 5} pb={5}>
        <Button variant={isFormVisible ? 'outlined' : 'contained'} onClick={toggleManualForm} data-testid="new-manual-address">
          {isFormVisible ? `< Back to autofill` : `Add address manually`}
        </Button>
      </Box>
      {isFormVisible && (
        <AddNewAddressForm
          availableSuburbs={availableSuburbs ?? []}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          onSubmit={onSubmit}
          register={register}
          setValue={setValue}
        />
      )}
    </>
  );
};

export default AddNewAddress;
